import React, { useEffect, useState } from 'react';
import { ClipboardList, Stethoscope, ShieldCheck, Brain } from 'lucide-react';
import { useInView } from '../hooks/useInView';

// Capability stats only — no kg / weeks / satisfaction claims (spec sec. 9 + 31:
// "do not promise a specific kg amount", "do not publish fabricated reviews").
const STATS = [
  { icon: ClipboardList, value: 2,   suffix: ' min', label: 'AI-assisted assessment' },
  { icon: Stethoscope,   value: 100, suffix: '%',    label: 'Licensed doctor review before treatment' },
  { icon: ShieldCheck,   value: 100, suffix: '%',    label: 'Pharmacy fulfilment via licensed partners' },
  { icon: Brain,         value: 24,  suffix: '/7',   label: 'AI-assisted progress support' },
];

// Counts up to the target value over ~1.2s once the element scrolls into view.
function useCountUp(target, { duration = 1200, decimals = 0, enabled = false } = {}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let raf = 0;
    const start = performance.now();
    function tick(now) {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic for a slightly springy feel
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, enabled]);
  return decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
}

function StatCell({ stat, index, inView }) {
  const display = useCountUp(stat.value, { decimals: stat.decimals || 0, enabled: inView });
  const Icon = stat.icon;
  return (
    <div
      className={['flex flex-col items-center text-center transition-all duration-700 ease-out',
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      ].join(' ')}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand-50 text-brand-700 mb-3">
        <Icon size={22} />
      </span>
      <div className="text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900 tabular-nums">
        {display}<span className="text-ink-500 text-2xl sm:text-3xl font-medium">{stat.suffix}</span>
      </div>
      <div className="mt-1.5 text-sm text-ink-500 max-w-[14rem]">{stat.label}</div>
    </div>
  );
}

export default function StatsBlock() {
  const [ref, inView] = useInView({ threshold: 0.25 });
  return (
    <section className="bg-white border-y border-ink-100">
      <div ref={ref} className="container-x py-16 sm:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
          {STATS.map((s, i) => (
            <StatCell key={s.label} stat={s} index={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}
